import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { lowerCase, startCase } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { PillSelect } from "@whoosh/design-system/src/components/controls/PillSelect";
import { TextArea } from "@whoosh/design-system/src/components/controls/TextArea";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { UpdateDiningBookingInput } from "../../../../hooks/__generated__/useUpdateDiningBookingDataMutation.graphql";
import { useUpdateDiningBookingData } from "../../../../hooks/useUpdateDiningBookingData";
import { DiningSpecialRequestModalHeader } from "./DiningSpecialRequestModalHeader";
import type {
  DietaryTags,
  DiningSpecialRequestsModal_booking$key,
  OccasionTags,
} from "./__generated__/DiningSpecialRequestsModal_booking.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bookingRef: DiningSpecialRequestsModal_booking$key | null;
  ownerId: string | null;
  otherPatronIds: string[];
};

export const DiningSpecialRequestsModal = ({
  isOpen,
  onClose,
  bookingRef,
  ownerId,
  otherPatronIds,
}: Props) => {
  const booking = useFragment(DiningSpecialRequestsModalFragment, bookingRef);
  const bookingId = booking?.id;
  const diningReservationHoldId = booking?.diningReservationHold?.id ?? null;

  const originalDietaryTags = useMemo(
    () => booking?.dietaryTags ?? [],
    [booking]
  );
  const originalOccasionTags = useMemo(
    () => booking?.occasionTags ?? [],
    [booking]
  );
  const originalNotes = booking?.notesFromMember ?? "";

  const [dietaryTags, setDietaryTags] = useState<DietaryTags[]>(() => [
    ...originalDietaryTags,
  ]);
  const [occasionTags, setOccasionTags] = useState<OccasionTags[]>(() => [
    ...originalOccasionTags,
  ]);
  const [notes, setNotes] = useState<string>(originalNotes);
  const [updateDiningBooking, isUpdating] = useUpdateDiningBookingData();

  useEffect(() => {
    if (isOpen) {
      setDietaryTags([...originalDietaryTags]);
      setOccasionTags([...originalOccasionTags]);
      setNotes(originalNotes);
    }
  }, [isOpen, originalDietaryTags, originalOccasionTags, originalNotes]);

  const dietaryTagOptions = useMemo(
    () =>
      (booking?.startSlot?.agenda?.facility?.diningDietaryTags ?? [])
        .filter((tag): tag is DietaryTags => Boolean(tag))
        .map((tag) => ({
          value: tag,
          label: startCase(lowerCase(tag)),
        })),
    [booking]
  );

  const occasionTagOptions = useMemo(
    () =>
      (booking?.startSlot?.agenda?.facility?.diningOccasionTags ?? [])
        .filter((tag): tag is OccasionTags => Boolean(tag))
        .map((tag) => ({
          value: tag,
          label: startCase(lowerCase(tag)),
        })),
    [booking]
  );

  const arraysEqual = useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  }, []);

  const isDirty =
    !arraysEqual([...dietaryTags], [...originalDietaryTags]) ||
    !arraysEqual([...occasionTags], [...originalOccasionTags]) ||
    notes !== originalNotes;

  const isDraft = booking?.status === "DRAFT";

  const handleSave = useCallback(() => {
    if (!bookingId || !ownerId) {
      return;
    }
    const diningBookignInput: UpdateDiningBookingInput = {
      bookingId,
      ownerId,
      otherPatronIds,
      notes,
      dietaryTags,
      occasionTags,
      ...(isDraft ? { reservationHoldId: diningReservationHoldId } : {}),
    };

    updateDiningBooking({
      input: diningBookignInput,
      onCompleted: () => {
        onClose();
      },
    });
  }, [
    bookingId,
    ownerId,
    otherPatronIds,
    notes,
    dietaryTags,
    occasionTags,
    isDraft,
    diningReservationHoldId,
    updateDiningBooking,
    onClose,
  ]);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      disableContentScroll={true}
      hideCloseButton={true}
      upperNode={<DiningSpecialRequestModalHeader onClose={onClose} />}
      content={
        <VStack flex={1} backgroundColor="surface-secondary">
          <ScrollView style={{ flex: 1 }}>
            <VStack gap={5} paddingVertical={2}>
              <VStack gap={4} backgroundColor="surface-primary" padding={5}>
                <VStack gap={3}>
                  <Text variant="mainBaseSemiBold">
                    {t`Does your party have any allergies?`}
                  </Text>
                  <PillSelect
                    options={dietaryTagOptions}
                    values={dietaryTags}
                    setValues={setDietaryTags}
                    allowAddition={false}
                    placeholder={t`Search dietary tags`}
                  />
                </VStack>

                <VStack gap={3}>
                  <Text variant="mainBaseSemiBold">
                    {t`Are you celebrating a special occasion?`}
                  </Text>
                  <PillSelect
                    options={occasionTagOptions}
                    values={occasionTags}
                    setValues={setOccasionTags}
                    allowAddition={false}
                    placeholder={t`Search occasion tags`}
                  />
                </VStack>

                <VStack gap={3}>
                  <Text variant="mainBaseSemiBold">
                    {t`Other special requests?`}
                  </Text>
                  <TextArea
                    name="notes"
                    value={notes}
                    onChange={setNotes}
                    placeholder={t`Let us know if you have any special requests`}
                  />
                </VStack>
              </VStack>
            </VStack>
          </ScrollView>
          {isDirty ? (
            <Box
              borderTopWidth={1}
              borderTopColor="surface-dividers"
              padding={6}
            >
              <Button
                onPress={handleSave}
                accessibilityLabel={t`Save changes`}
                isLoading={isUpdating}
              >
                {t`Save Changes`}
              </Button>
            </Box>
          ) : null}
        </VStack>
      }
    />
  );
};

const DiningSpecialRequestsModalFragment = graphql`
  fragment DiningSpecialRequestsModal_booking on Booking {
    id
    ... on DiningBooking {
      dietaryTags
      occasionTags
      notesFromMember
      diningReservationHold {
        id
      }
      status
      startSlot {
        agenda {
          facility {
            diningDietaryTags
            diningOccasionTags
          }
        }
      }
    }
  }
`;

export { DiningSpecialRequestsModalFragment };
